import React, { FC, useState } from 'react';
import {
  AccidentDataType,
  GetDetailsResponseType,
} from 'QuoteApp/services/endpoints/GetDetails';
import * as Yup from 'yup';
import { ReportDetailRow } from '../../ReportDetailRow/ReportDetailRow';
import { useToast } from 'Shared/components';
import { ChallengeForm } from '../../ChallengeForm';
import { useAppContext } from 'QuoteApp/services/providers';
import { Formik } from 'formik';
import { AccidentChallengeFormValuesType } from 'QuoteApp/services/endpoints/PostChallengeAccident';
import { useLazyQuoteApi } from 'Shared/services/Api';
import { useQuoteAppFeatureFlags } from 'QuoteApp/utils/hooks/useQuoteAppFeatureFlags';
import {
  getChallengeAccidentFormValidation,
  getChallengeAccidentInitialFormValues,
  onSubmitClaimChallenge,
} from '../Accidents/AccidentForm';
import {
  ReportChangeMessageType,
  ReportMessages,
} from 'QuoteApp/screens/Reports';
import { useEdit } from 'QuoteApp/utils/contexts';
import { AccidentChallengeForm } from '../../AccidentChallengeForm';
import styles from './styles.module.css';

type LendingLossRowType = FC<{
  lendingLoss: AccidentDataType;
  canChallenge: boolean;
  lendingLossIdForDriverId: {
    driverId: string;
    accidentId: string;
  }[];
}>;

export const LendingLossRow: LendingLossRowType = ({
  lendingLoss,
  canChallenge,
  lendingLossIdForDriverId,
}) => {
  const { details, config, setDetails, fetchRate, rate } = useAppContext();

  const {
    selfReportedAccidentsForClassificationsFlag,
    collectDayOfAccidentFlag,
  } = useQuoteAppFeatureFlags();

  const { editingId, changeEditingId } = useEdit();

  const [messages, setMessages] = useState<Array<ReportChangeMessageType>>([]);

  const { toast } = useToast();

  const [, challengeAccident] = useLazyQuoteApi<GetDetailsResponseType>({});

  if (!config) return null;

  const driverIdFromLendingLoss = lendingLossIdForDriverId.find(
    ({ accidentId }) => accidentId === lendingLoss.identifier
  )?.driverId;

  const challengeCommand = details.commands.drivers[
    driverIdFromLendingLoss ?? ''
  ].accidents[lendingLoss.identifier].find(
    c => c.name === 'challenge_accident'
  );

  return (
    <Formik<AccidentChallengeFormValuesType>
      enableReinitialize
      initialValues={getChallengeAccidentInitialFormValues(
        lendingLoss,
        lendingLoss.identifier
      )}
      validationSchema={Yup.object().shape(
        getChallengeAccidentFormValidation(
          lendingLoss.identifier,
          selfReportedAccidentsForClassificationsFlag,
          collectDayOfAccidentFlag
        )
      )}
      onSubmit={async values => {
        await onSubmitClaimChallenge(
          values,
          lendingLoss.identifier,
          challengeAccident,
          challengeCommand,
          fetchRate,
          rate,
          setMessages,
          changeEditingId,
          setDetails,
          toast
        );
      }}
    >
      {({ values, setFieldValue, errors, resetForm }) => {
        const isEditing =
          editingId === lendingLoss.identifier &&
          values[lendingLoss.identifier].challengeType === 'edit';

        return (
          <>
            <ReportDetailRow
              identifier={lendingLoss.identifier}
              challengeForm={
                <ChallengeForm
                  variant={values[lendingLoss.identifier].challengeType}
                  identifier={lendingLoss.identifier}
                  type="accidents"
                  challengeReasonConfig={
                    config.drivers.accidents.challengeReasonOptions
                  }
                />
              }
              source={lendingLoss.atFault.source}
              canChallenge={canChallenge}
              challengeCommand={challengeCommand}
              includeRemove
              checkmarked={null}
              claimStatus={lendingLoss.claimStatus.value}
              onRemove={() => {
                resetForm();
                setFieldValue(
                  `${lendingLoss.identifier}.challengeType`,
                  'remove'
                );
              }}
              onEdit={() => {
                resetForm();
                setFieldValue(
                  `${lendingLoss.identifier}.challengeType`,
                  'edit'
                );
              }}
              type="lending-loss"
            >
              <AccidentChallengeForm
                accident={lendingLoss}
                identifier={lendingLoss.identifier}
                values={values}
                isEditing={isEditing}
                setFieldValue={setFieldValue}
                errors={errors}
                isLendingLoss
              />
            </ReportDetailRow>
            <ReportMessages messages={messages} className={styles.message} />
          </>
        );
      }}
    </Formik>
  );
};
